<template>
     <v-row>
    <v-col>
  <v-container fluid>
    <v-row>
      <v-col class="d-flex flex-row align-center flex-wrap" style="gap: 8px;">
        <v-btn text @click="()=>{this.$router.go(-1)}"><v-icon dark>mdi-arrow-left</v-icon></v-btn>
        <h1>Product/Inventory Configurator</h1>
        <v-progress-circular
            indeterminate
            color="green"
            v-if="loaders.form"
        ></v-progress-circular>
        <v-btn small color="success" :loading="loaders.saveAll" :disabled="loaders.form" @click="saveAll">Save All</v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12"><h2>Global Settings</h2></v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-form :disabled="loaders.form">
          <v-card outlined>
            <v-card-text>
              <v-text-field label="Quantity Precision" v-model="data.globals.quantityPrecision" outlined dense/>
              <span class="d-flex flex-row flex-wrap">
                <v-checkbox label="Default Allow Fractional?" v-model="data.globals.defaultAllowFractional" outlined dense/>
                <v-text-field label="Default Factor of Quantity" hint="This is force converted using the Quantity Precision so make sure to adjust the Quantity Precision AND SAVE accordingly first." persistent-hint v-model="data.globals.defaultFactorOfQuantity" outlined dense/>
              </span>
              <v-checkbox label="Enforce Min Sale Quantity?" v-model="data.globals.enforceMinSaleQuantity" outlined dense/>
            </v-card-text>
          </v-card>
          <v-card outlined>
            <v-card-text>
              <v-checkbox label="Is Tax Type Editable?" v-model="data.globals.productTaxTypeEditable" outlined dense/>
              <v-select label="Default Tax Type" v-model="data.globals.productTaxTypeDefaultValue" :items="data.taxTypes" outlined dense/>
              <v-checkbox label="Show Minimal Tax Type?" v-model="data.globals.productTaxTypeMinimal" outlined dense/>
              <v-select label="Minimal Tax Types (Only 2)" v-model="data.globals.productTaxTypeMinimalValues" :items="data.taxTypes" clearable outlined dense multiple/>
            </v-card-text>
          </v-card>
        </v-form>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-text>
            <v-checkbox label="Show Quantity on Product Index?" v-model="data.globals.showQuantityOnProductIndex" outlined dense/>
            <v-checkbox label="Unit Conversions: allow rounding each unit up before multiplying?" v-model="data.globals.unitConversionRoundPerUnit" outlined dense hide-details
                        hint="When on, products using a unit conversion can be set to charge per rounded-up unit (10ft 2in x 5 is billed as 11ft x 5 = 55ft, not 51ft). When off, that product setting is hidden and ignored."/>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="my-6"/>

    <v-row>
      <v-col cols="12">
        <h2>Bulk Product Settings</h2>
        <span class="caption">Find products, choose which of the matches to change, then overwrite one or more settings on just those products. Nothing here changes the global settings above.</span>
      </v-col>
    </v-row>

    <!-- 1. Filter -->
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="subtitle-1">1. Find products <span class="caption ml-2">(leave everything empty to list all products)</span></v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4"><v-text-field v-model="filters.search" label="Name, SKU, barcode or VGC contains" outlined dense clearable hide-details @keyup.enter="searchProducts"/></v-col>
              <v-col cols="6" md="2"><v-text-field v-model="filters.priceMin" label="Regular price from" type="number" outlined dense clearable hide-details/></v-col>
              <v-col cols="6" md="2"><v-text-field v-model="filters.priceMax" label="Regular price to" type="number" outlined dense clearable hide-details/></v-col>
              <v-col cols="12" md="4"><v-select v-model="filters.types" :items="productTypes" label="Product type" multiple chips small-chips deletable-chips outlined dense clearable hide-details/></v-col>
              <v-col cols="12" md="4"><v-autocomplete v-model="filters.brandIds" :items="options.brands" item-text="name" item-value="id" label="Brand" multiple chips small-chips deletable-chips outlined dense clearable hide-details/></v-col>
              <v-col cols="12" md="4"><v-autocomplete v-model="filters.categoryIds" :items="options.categories" item-text="name" item-value="id" label="Category" multiple chips small-chips deletable-chips outlined dense clearable hide-details/></v-col>
              <v-col cols="12" md="4"><v-autocomplete v-model="filters.tagIds" :items="options.tags" item-text="name" item-value="id" label="Tag" multiple chips small-chips deletable-chips outlined dense clearable hide-details/></v-col>
              <v-col cols="12" md="4"><v-autocomplete v-model="filters.supplierIds" :items="options.suppliers" item-text="name" item-value="id" label="Supplier" multiple chips small-chips deletable-chips outlined dense clearable hide-details/></v-col>
              <v-col cols="12" md="4"><v-autocomplete v-model="filters.taxTypeIds" :items="options.taxTypes" item-text="name" item-value="id" label="Tax type" multiple chips small-chips deletable-chips outlined dense clearable hide-details/></v-col>
              <v-col cols="12" md="8"><v-autocomplete v-model="filters.unitConversionIds" :items="conversionFilterItems" item-text="name" item-value="id" label="Unit conversion" multiple chips small-chips deletable-chips outlined dense clearable hide-details/></v-col>
              <v-col v-for="flag in flagFilters" :key="flag.key" cols="6" md="2">
                <v-select v-model="filters[flag.key]" :items="triStateItems" :label="flag.label" outlined dense hide-details/>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="pl-4 pb-4">
            <v-btn color="info" :loading="loaders.search" @click="searchProducts">Find products</v-btn>
            <v-btn text class="ml-2" @click="clearFilters">Clear filters</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- 2. Matching products: list (7) + read-only product view (5) -->
    <v-row v-if="searched">
      <v-col cols="12" md="7">
        <v-card outlined>
          <v-card-title class="subtitle-1 d-flex flex-wrap align-center" style="gap: 8px;">
            <span>2. Choose products</span>
            <span class="caption">{{results.length}} found, <b>{{selected.length}} selected</b></span>
            <v-spacer/>
            <v-btn small text @click="selectAll">Select all</v-btn>
            <v-btn small text @click="selected = []">Select none</v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="capped" dense text type="warning">Only the first {{limit}} matching products are listed. Narrow the filter to see the rest.</v-alert>
            <v-data-table
                v-model="selected"
                :items="results"
                :headers="resultHeaders"
                item-key="id"
                show-select
                dense
                :items-per-page="50"
                :footer-props="{'items-per-page-options': [25, 50, 100, 250, {text: 'All', value: -1}]}"
                :loading="loaders.search"
                :item-class="rowClass"
                no-data-text="No products match this filter."
                @click:row="row => activeId = row.id"
            >
              <template v-slot:item.regularPrice="{ item }">{{ item.regularPrice }}</template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="5">
        <div style="position: sticky; top: 70px; max-height: calc(100vh - 90px); overflow-y: auto;">
          <product-summary-pane :product-id="activeId" can-import @import-settings="importSettings"/>
        </div>
      </v-col>
    </v-row>

    <!-- 3. Values to overwrite -->
    <v-row v-if="searched">
      <v-col cols="12" id="overwritePane">
        <v-card outlined>
          <v-card-title class="subtitle-1">3. Choose what to overwrite <span class="caption ml-2">(only ticked values are changed, on the selected products)</span></v-card-title>
          <v-card-text>
            <div v-for="group in overwriteGroups" :key="group.title" class="mb-4">
              <h4 class="mb-1">{{group.title}}</h4>
              <div v-for="r in group.rows" :key="r.key" class="d-flex flex-row align-center flex-wrap mb-1" style="gap: 12px;">
                <v-checkbox v-model="overwrite[r.key].enabled" :disabled="!!lockedReason(r.key)" :label="r.label" hide-details class="mt-0" style="min-width: 260px;"/>
                <v-checkbox v-if="r.type==='bool'" v-model="overwrite[r.key].value" :disabled="!overwrite[r.key].enabled" :label="r.valueLabel || 'Yes'" hide-details class="mt-0"/>
                <v-text-field v-else-if="r.type==='number' || r.type==='text'" v-model="overwrite[r.key].value" :disabled="!overwrite[r.key].enabled" :type="r.type" :label="r.label" outlined dense hide-details style="max-width: 260px;"/>
                <v-select v-else-if="r.type==='select'" v-model="overwrite[r.key].value" :disabled="!overwrite[r.key].enabled" :items="r.items" item-text="name" item-value="id" :label="r.label" outlined dense hide-details style="max-width: 300px;"/>
                <v-btn v-if="r.globalDefault && !lockedReason(r.key)" x-small text :disabled="!overwrite[r.key].enabled" @click="overwrite[r.key].value = r.globalDefault()">Use global default</v-btn>
                <span v-if="lockedReason(r.key)" class="caption warning--text">{{lockedReason(r.key)}}</span>
                <span v-else-if="r.hint" class="caption">{{r.hint}}</span>
              </div>
            </div>

            <h4 class="mb-1">Used in multiples</h4>
            <div class="d-flex flex-row align-center flex-wrap mb-4" style="gap: 12px;">
              <v-checkbox v-model="overwrite.multiples.enabled" :disabled="serialsForceOff" label="Set Used In Multiples" hide-details class="mt-0" style="min-width: 260px;"/>
              <v-select v-model="overwrite.multiples.mode" :disabled="!overwrite.multiples.enabled || serialsForceOff" :items="multiplesModes" item-text="text" item-value="value" label="Format" outlined dense hide-details style="max-width: 230px;"/>
              <v-autocomplete v-if="overwrite.multiples.mode==='conversion'" v-model="overwrite.multiples.unitConversionId" :disabled="!overwrite.multiples.enabled" :items="options.unitConversions" item-text="name" item-value="id" label="Unit conversion" outlined dense hide-details style="max-width: 460px;"/>
              <v-text-field v-if="overwrite.multiples.mode==='label'" v-model="overwrite.multiples.multipleLabel" :disabled="!overwrite.multiples.enabled" label="Multiple label" outlined dense hide-details style="max-width: 230px;"/>
              <v-checkbox v-if="overwrite.multiples.mode==='conversion' && chosenConversion && chosenConversion.priceUnit" v-model="overwrite.multiples.roundUp" :disabled="!overwrite.multiples.enabled" @change="onRoundUpChange" :label="`Charge by the whole ${chosenConversion.priceUnit}`" hide-details class="mt-0"/>
              <v-checkbox v-if="overwrite.multiples.mode==='conversion' && chosenConversion && chosenConversion.priceUnit && data.globals.unitConversionRoundPerUnit" v-model="overwrite.multiples.roundPerUnit" :disabled="!overwrite.multiples.enabled" @change="onPerUnitChange" label="Round each unit up before multiplying" hide-details class="mt-0"/>
            </div>
            <div v-if="serialsForceOff" class="caption warning--text mb-2">Requires Serials is being turned on, which turns Used In Multiples off for these products. Untick it to set a multiples format.</div>
            <div v-if="overwrite.multiples.enabled && overwrite.multiples.mode==='conversion'" class="caption mb-4">The conversion also sets each product's unit type, factor of quantity, fractional rule and (unless you also overwrite it below) minimum order quantity, as picking it on the product page does.</div>

            <h4 class="mb-1">Categories and tags</h4>
            <div v-for="r in listRows" :key="r.key" class="d-flex flex-row align-center flex-wrap mb-1" style="gap: 12px;">
              <v-checkbox v-model="overwrite[r.key].enabled" :label="r.label" hide-details class="mt-0" style="min-width: 260px;"/>
              <v-select v-model="overwrite[r.key].mode" :disabled="!overwrite[r.key].enabled" :items="listModes" item-text="text" item-value="value" label="How" outlined dense hide-details style="max-width: 170px;"/>
              <v-autocomplete v-model="overwrite[r.key].ids" :disabled="!overwrite[r.key].enabled" :items="options[r.key]" item-text="name" item-value="id" :label="r.label" multiple chips small-chips deletable-chips outlined dense hide-details style="max-width: 560px;"/>
            </div>

            <div class="caption mt-4">Products that use a unit conversion get Allow Fractional, Factor of Quantity and Unit Type from their conversion, so those are skipped for them.</div>
            <div v-if="incompleteReason" class="caption error--text mt-2">{{incompleteReason}}</div>
          </v-card-text>
          <v-card-actions class="pl-4 pb-4">
            <v-btn color="warning" :disabled="selected.length === 0 || enabledKeys.length === 0 || !!incompleteReason" @click="confirmDialog = true">Overwrite {{enabledKeys.length}} value(s) on {{selected.length}} product(s)</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="confirmDialog" max-width="560">
      <v-card>
        <v-card-title>Overwrite products?</v-card-title>
        <v-card-text>
          <p><b>{{selected.length}}</b> selected product(s) will be changed:</p>
          <ul>
            <li v-for="line in changeSummary" :key="line">{{line}}</li>
          </ul>
          <p class="mt-3 caption">This replaces whatever those products have now and cannot be undone from here.</p>
        </v-card-text>
        <v-card-actions class="pl-5 pb-5">
          <v-btn text color="error" @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="success" :loading="loaders.apply" @click="applyOverwrite">Confirm overwrite</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
    </v-col>
   </v-row>
</template>
<script>
import { mapGetters } from 'vuex'
import axios from "axios";
import utils from "../../plugins/helpers"
import ProductSummaryPane from "../../components/ProductSummaryPane.vue"

export default {
  name: 'Home',
  components: {ProductSummaryPane},
  data () {
    return {
      utils: utils,

      isEditable: false,
      isCreate: true,

      requiredRules: [
        v => !!v || 'Field is required.'
      ],

      data: {
        globals: {},
        taxTypes: [],
        filterOptions: {}
      },
      validForm: false,

      loaders: {
        form: false,
        saveAll: false,
        search: false,
        apply: false
      },

      // bulk product settings
      filters: {
        search: '', priceMin: null, priceMax: null,
        brandIds: [], categoryIds: [], tagIds: [], supplierIds: [], taxTypeIds: [], unitConversionIds: [], types: [],
        allowFractional: null, requireSerials: null, usedInMultiples: null, isComposite: null, manageStock: null
      },
      flagFilters: [
        {key: 'allowFractional', label: 'Allow fractional'},
        {key: 'requireSerials', label: 'Requires serials'},
        {key: 'usedInMultiples', label: 'Used in multiples'},
        {key: 'isComposite', label: 'Composite'},
        {key: 'manageStock', label: 'Manages stock'}
      ],
      triStateItems: [{text: 'Any', value: null}, {text: 'Yes', value: true}, {text: 'No', value: false}],
      productTypes: ['simple', 'variable', 'variation'],
      searched: false,
      capped: false,
      limit: 0,
      results: [],
      selected: [],
      confirmDialog: false,
      activeId: null,
      multiplesModes: [
        {text: 'Off (not used in multiples)', value: 'off'},
        {text: 'Unit conversion', value: 'conversion'},
        {text: 'Custom multiple (label)', value: 'label'}
      ],
      listModes: [
        {text: 'Add these', value: 'add'},
        {text: 'Remove these', value: 'remove'},
        {text: 'Replace all with these', value: 'replace'}
      ],
      listRows: [{key: 'categories', label: 'Categories'}, {key: 'tags', label: 'Tags'}],
      serialsAutoOffedMultiples: false,
      overwrite: {
        allowFractional: {enabled: false, value: false},
        quantityStepAmount: {enabled: false, value: 1},
        minSaleQuantity: {enabled: false, value: 1},
        packagingQuantity: {enabled: false, value: 1},
        unit: {enabled: false, value: 'unit'},
        warrantyInformation: {enabled: false, value: ''},
        requireSerials: {enabled: false, value: false},
        lockMultipleAmount: {enabled: false, value: true},
        ecomVisible: {enabled: false, value: false},
        taxType: {enabled: false, value: null},
        brandId: {enabled: false, value: null},
        multiples: {enabled: false, mode: 'conversion', unitConversionId: null, multipleLabel: '', roundUp: true, roundPerUnit: false},
        categories: {enabled: false, mode: 'add', ids: []},
        tags: {enabled: false, mode: 'add', ids: []}
      },
      resultHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'SKU', value: 'sku' },
        { text: 'Price', value: 'regularPrice' }
      ],

      taxTypes: [],

      headers: [
        { text: 'ID', align: 'start', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'Hidden', value: 'hidden' },
        { text: 'Transactions', value: 'numTransactions' }
      ]
    }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    options(){
      let o = this.data.filterOptions || {};
      return {brands: o.brands || [], categories: o.categories || [], tags: o.tags || [], suppliers: o.suppliers || [], taxTypes: o.taxTypes || [], unitConversions: o.unitConversions || []};
    },
    conversionFilterItems(){
      return [{id: 'none', name: 'No unit conversion'}, ...this.options.unitConversions];
    },
    chosenConversion(){
      return this.options.unitConversions.find(x => x.id === this.overwrite.multiples.unitConversionId) || null;
    },
    // the simple one-value-per-row overwrite settings, grouped for the page
    overwriteGroups(){
      let g = this.data.globals || {};
      let brandItems = [{id: 'none', name: '(no brand)'}, ...this.options.brands];
      return [
        {title: 'Quantities', rows: [
          {key: 'allowFractional', label: 'Allow Fractional', type: 'bool', valueLabel: 'Allowed', globalDefault: () => !!g.defaultAllowFractional},
          {key: 'quantityStepAmount', label: 'Factor of Quantity', type: 'number', globalDefault: () => g.defaultFactorOfQuantity},
          {key: 'minSaleQuantity', label: 'Minimum Order Quantity', type: 'number'},
          {key: 'packagingQuantity', label: 'Packaging Quantity', type: 'number'},
          {key: 'unit', label: 'Unit Type', type: 'text'}
        ]},
        {title: 'Tax, brand and flags', rows: [
          {key: 'taxType', label: 'Tax Type', type: 'select', items: this.options.taxTypes, globalDefault: () => parseInt(g.productTaxTypeDefaultValue)},
          {key: 'brandId', label: 'Brand', type: 'select', items: brandItems},
          {key: 'requireSerials', label: 'Requires Serials', type: 'bool', hint: 'Turns Used In Multiples off for these products.'},
          {key: 'lockMultipleAmount', label: 'Lock multiple value on Returns/Dispatch', type: 'bool'},
          {key: 'ecomVisible', label: 'Visible on ecommerce', type: 'bool'},
          {key: 'warrantyInformation', label: 'Warranty Information', type: 'text'}
        ]}
      ];
    },
    // Requires Serials = Yes forces Used In Multiples off (same as the product page)
    serialsForceOff(){
      return this.overwrite.requireSerials.enabled && this.overwrite.requireSerials.value === true;
    },
    // a unit conversion dictates the unit type, factor of quantity and fractional rule
    conversionSetsRules(){
      return this.overwrite.multiples.enabled && this.overwrite.multiples.mode === 'conversion';
    },
    multiplesOff(){
      return this.serialsForceOff || (this.overwrite.multiples.enabled && this.overwrite.multiples.mode === 'off');
    },
    incompleteReason(){
      let m = this.overwrite.multiples;
      if(m.enabled && m.mode === 'conversion' && !m.unitConversionId) return 'Choose a unit conversion.';
      if(m.enabled && m.mode === 'label' && !(m.multipleLabel || '').trim()) return 'Enter a multiple label.';
      return null;
    },
    enabledKeys(){
      return Object.keys(this.overwrite).filter(k => this.overwrite[k].enabled);
    },
    changeSummary(){
      let o = this.overwrite;
      let names = {};
      this.overwriteGroups.forEach(grp => grp.rows.forEach(r => { names[r.key] = r; }));
      let lines = [];
      for(let key of this.enabledKeys){
        let v = o[key];
        if(key === 'multiples'){
          let c = this.chosenConversion;
          lines.push(`Used In Multiples: ${v.mode === 'off' ? 'off' : (v.mode === 'label' ? `custom multiple "${v.multipleLabel}"` : (c ? c.name : '(choose a conversion)'))}`);
        }else if(key === 'categories' || key === 'tags'){
          let list = this.options[key].filter(x => v.ids.includes(x.id)).map(x => x.name).join(', ') || 'nothing';
          lines.push(`${key === 'tags' ? 'Tags' : 'Categories'}: ${{add: 'add', remove: 'remove', replace: 'replace all with'}[v.mode]} ${list}`);
        }else{
          let r = names[key];
          let shown = v.value;
          if(r.type === 'bool') shown = v.value ? 'Yes' : 'No';
          if(key === 'taxType') shown = this.taxTypeName(v.value);
          if(key === 'brandId') shown = v.value === 'none' || v.value === null ? '(no brand)' : (this.options.brands.find(x => x.id === v.value) || {}).name;
          lines.push(`${r.label}: ${shown}`);
        }
      }
      return lines;
    }
  },
  watch: {
    serialsForceOff(on){
      let m = this.overwrite.multiples;
      if(on){
        // turning Requires Serials on switches Used In Multiples off, so that edit is enabled and set to Off
        this.serialsAutoOffedMultiples = !m.enabled;
        m.enabled = true;
        m.mode = 'off';
        this.overwrite.lockMultipleAmount.enabled = false;
      }else if(this.serialsAutoOffedMultiples){
        m.enabled = false;
        this.serialsAutoOffedMultiples = false;
      }
    },
    conversionSetsRules(on){
      if(on){
        for(let key of ['allowFractional', 'quantityStepAmount', 'unit']) this.overwrite[key].enabled = false;
      }
    },
    multiplesOff(on){
      if(on) this.overwrite.lockMultipleAmount.enabled = false;
    }
  },
  async mounted(){
    await this.fetchData();
  },
  methods: {
    async fetchData(){
      try{
        this.loaders.form = true;

        let res = await axios.get(`${this.getEndpoint}/api/inventoryConfigurator`)
        this.data = res.data.data

        console.log(this.data)
      }
      finally {
        this.loaders.form = false;
      }
    },
    taxTypeName(id){
      let t = this.options.taxTypes.find(x => x.id === parseInt(id));
      return t ? t.name : (id == null ? '' : `#${id}`);
    },
    conversionName(id){
      let c = this.options.unitConversions.find(x => x.id === parseInt(id));
      return c ? c.name : `#${id}`;
    },
    clearFilters(){
      this.filters = {
        search: '', priceMin: null, priceMax: null,
        brandIds: [], categoryIds: [], tagIds: [], supplierIds: [], taxTypeIds: [], unitConversionIds: [], types: [],
        allowFractional: null, requireSerials: null, usedInMultiples: null, isComposite: null, manageStock: null
      };
    },
    // Fills the overwrite section from one product's stored values and ticks every value it has. Untick whatever should not change.
    // Values that are decided by another value follow the same rules as typing them in (a unit conversion sets the unit type, factor and
    // fractional rule; Requires Serials turns Used In Multiples off; Used In Multiples off leaves nothing to lock), so those stay unticked.
    importSettings(p){
      let m = p.metadata || {};
      let o = this.overwrite;
      let on = (key, value) => { o[key].enabled = true; o[key].value = value; };
      let has = v => v !== undefined && v !== null && v !== '';

      on('allowFractional', m.allowFractional === true);
      if(has(m.quantityStepAmount)) on('quantityStepAmount', m.quantityStepAmount);
      if(has(m.minSaleQuantity)) on('minSaleQuantity', m.minSaleQuantity);
      if(has(m.packagingQuantity)) on('packagingQuantity', m.packagingQuantity);
      if(has(m.unit)) on('unit', m.unit);
      if(has(m.warrantyInformation)) on('warrantyInformation', m.warrantyInformation);
      on('requireSerials', m.requireSerials === true);
      on('lockMultipleAmount', m.lockMultipleAmount !== false);
      on('ecomVisible', !!p.ecomVisible);
      if(p.taxTypeId) on('taxType', p.taxTypeId);
      on('brandId', p.brandId || 'none');

      let mu = o.multiples;
      mu.enabled = true;
      if(!m.usedInMultiples || m.requireSerials === true){
        mu.mode = 'off';
      }else if(m.unitConversionId != null){
        mu.mode = 'conversion';
        let known = this.options.unitConversions.some(x => x.id === parseInt(m.unitConversionId));
        mu.unitConversionId = known ? parseInt(m.unitConversionId) : null;
        mu.roundUp = m.roundUpConversionQuantity !== false;
        mu.roundPerUnit = m.roundUpPerUnitConversion === true;
      }else{
        mu.mode = 'label';
        mu.multipleLabel = m.multipleLabel || '';
      }

      o.categories.enabled = true; o.categories.mode = 'replace'; o.categories.ids = [...(p.categoryIds || [])];
      o.tags.enabled = true; o.tags.mode = 'replace'; o.tags.ids = [...(p.tagIds || [])];

      this.serialsAutoOffedMultiples = false;
      // let the dependency rules settle, then re-apply them (they only fire on a change, and some may already have been in effect)
      this.$nextTick(() => {
        if(this.conversionSetsRules) for(let key of ['allowFractional', 'quantityStepAmount', 'unit']) o[key].enabled = false;
        if(this.multiplesOff) o.lockMultipleAmount.enabled = false;
        this.$snack(`Settings copied from "${p.name}". Everything it has is ticked - untick what should not change.`, "success");
        let el = document.getElementById('overwritePane');
        if(el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
      });
    },
    // why a row cannot be overwritten right now (it is decided by another value that is being overwritten)
    lockedReason(key){
      if(this.conversionSetsRules && ['allowFractional', 'quantityStepAmount', 'unit'].includes(key)) return 'Set by the unit conversion below (Used in multiples).';
      if(key === 'lockMultipleAmount' && this.multiplesOff) return 'Not used while Used In Multiples is off.';
      return null;
    },
    onRoundUpChange(v){
      if(!v) this.overwrite.multiples.roundPerUnit = false;
    },
    onPerUnitChange(v){
      if(v) this.overwrite.multiples.roundUp = true;
    },
    rowClass(item){
      return item.id === this.activeId ? 'primary lighten-5' : '';
    },
    selectAll(){
      this.selected = [...this.results];
    },
    async searchProducts(){
      try{
        this.loaders.search = true;
        let res = await axios.post(`${this.getEndpoint}/api/inventoryConfigurator/products/search`, {filters: this.filters});
        this.results = res.data.data;
        this.capped = res.data.capped;
        this.limit = res.data.limit;
        this.selected = [...this.results]; // everything found starts selected; untick the ones to leave alone
        this.searched = true;
      }
      finally {
        this.loaders.search = false;
      }
    },
    async applyOverwrite(){
      try{
        this.loaders.apply = true;

        let changes = {};
        let boolKeys = ['allowFractional', 'requireSerials', 'lockMultipleAmount', 'ecomVisible'];
        for(let key of this.enabledKeys){
          let o = this.overwrite[key];
          if(boolKeys.includes(key)) changes[key] = !!o.value;
          else if(['quantityStepAmount', 'minSaleQuantity', 'packagingQuantity'].includes(key)) changes[key] = o.value === '' ? null : Number(o.value);
          else if(key === 'multiples') changes[key] = {mode: o.mode, unitConversionId: o.unitConversionId, multipleLabel: o.multipleLabel, roundUp: o.roundUp, roundPerUnit: o.roundPerUnit};
          else if(key === 'categories' || key === 'tags') changes[key] = {mode: o.mode, ids: o.ids};
          else changes[key] = o.value; // unit, warrantyInformation, taxType, brandId
        }

        let res = await axios.put(`${this.getEndpoint}/api/inventoryConfigurator/products/apply`, {productIds: this.selected.map(x => x.id), changes});

        let skipped = Object.keys(res.data.skipped || {}).map(k => `${res.data.skipped[k]} skipped for ${k}`);
        this.$snack(`${res.data.updated} product(s) updated${skipped.length ? '. ' + skipped.join(', ') : ''}.`, "success");
        this.confirmDialog = false;

        // refresh the list so it shows the new values; keep the same products selected
        let keep = new Set(this.selected.map(x => x.id));
        await this.searchProducts();
        this.selected = this.results.filter(x => keep.has(x.id));
      }
      finally {
        this.loaders.apply = false;
      }
    },
    async saveAll(){
      try{
        this.loaders.form = true;

        for(let key of Object.keys(this.data.globals)){
          if(typeof this.data.globals[key]!=='boolean' && !Array.isArray(this.data.globals[key])){
            if(this.data.globals[key]===null || this.data.globals[key]===undefined || this.data.globals[key].length===0){
              throw `Value ${key} cannot be empty`;
            }
          }
        }

        if(parseInt(this.data.globals.quantityPrecision)!==parseFloat(this.data.globals.quantityPrecision)) throw "Quantity Precision must be an integer.";

        this.data.globals.defaultFactorOfQuantity = utils.pff(this.data.globals.defaultFactorOfQuantity, parseInt(this.data.globals.quantityPrecision))

        await axios.put(`${this.getEndpoint}/api/inventoryConfigurator/saveAll`, {data: this.data})

        this.$snack(`All saved!`, "success");

        await this.fetchData();
      }
      finally {
        this.loaders.form = false;
      }
    },
  }
}
</script>
